import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ConsentChecks, DonePanel, Field, GoldButton, ToolShell, inputCls, selectCls,
} from "@/components/FormUI";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL = "Autodiagnóstico orientativo basado en las respuestas del usuario. No constituye una due diligence ni una valoración.";

const SEMAFORO = { verde: "#4ade80", ambar: "#fbbf24", rojo: "#f87171" };

const Radar = ({ bloques }) => {
  const size = 260;
  const cx = size / 2;
  const cy = size / 2;
  const r = 95;
  const n = 5;
  const pt = (i, f) => {
    const ang = (-90 + (360 / n) * i) * (Math.PI / 180);
    return [cx + r * f * Math.cos(ang), cy + r * f * Math.sin(ang)];
  };
  const ring = (f) => Array.from({ length: n }, (_, i) => pt(i, f).join(",")).join(" ");
  const values = bloques.map((b) => b.pct / 100);
  const poly = values.map((v, i) => pt(i, Math.max(v, 0.04)).join(",")).join(" ");
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto w-full max-w-[300px]" data-testid="test-radar">
      {[0.33, 0.66, 1].map((f) => (
        <polygon key={f} points={ring(f)} fill="none" stroke="rgba(192,160,98,0.18)" strokeWidth="1" />
      ))}
      {Array.from({ length: n }, (_, i) => {
        const [x, y] = pt(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(192,160,98,0.18)" strokeWidth="1" />;
      })}
      <polygon points={poly} fill="rgba(192,160,98,0.22)" stroke="#C0A062" strokeWidth="2" />
      {bloques.map((b, i) => {
        const [x, y] = pt(i, 1.22);
        return (
          <text key={i} x={x} y={y} textAnchor="middle" fill="#8A97B8" fontSize="9.5" fontFamily="sans-serif">
            {b.nombre.split(" ").map((w, j) => (
              <tspan key={j} x={x} dy={j === 0 ? 0 : 11}>{w}</tspan>
            ))}
          </text>
        );
      })}
    </svg>
  );
};

export default function TestVenta() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [bloques, setBloques] = useState([]);
  const [idx, setIdx] = useState(0);
  const [respuestas, setRespuestas] = useState({});
  const [result, setResult] = useState(null);
  const [lead, setLead] = useState({ nombre: "", empresa: "", sector: "", email: "", telefono: "", consent_privacidad: false, consent_comercial: false });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Test: ¿Está tu empresa preparada para venderse? | AJG M&A Consulting";
    axios.get(`${API}/test-venta/questions`)
      .then(({ data }) => { setQuestions(data.questions); setBloques(data.bloques); })
      .catch(() => toast.error("No se pudo cargar el test. Recarga la página."));
  }, []);

  const answer = (qid, optIdx) => {
    const next = { ...respuestas, [qid]: optIdx };
    setRespuestas(next);
    if (idx < questions.length - 1) {
      setTimeout(() => setIdx(idx + 1), 250);
    } else {
      submitTest(next);
    }
  };

  const submitTest = async (answers) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/test-venta/evaluate`, { respuestas: answers });
      setResult(data);
    } catch {
      toast.error("No se pudo evaluar el test.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.consent_privacidad || !lead.sector) {
      toast.error("Completa todos los campos y acepta la política de privacidad.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/test-venta/lead`, {
        respuestas, nombre: lead.nombre, empresa: lead.empresa, sector: lead.sector,
        email: lead.email, telefono: lead.telefono,
        consent_privacidad: lead.consent_privacidad, consent_comercial: lead.consent_comercial,
      });
      setDone(true);
    } catch {
      toast.error("No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const q = questions?.[idx];
  const total = questions?.length || 15;
  const circ = 2 * Math.PI * 52;

  return (
    <ToolShell
      eyebrow="Herramientas · Test de preparación"
      title="¿Está tu empresa preparada para venderse?"
      intro="Un autodiagnóstico de 4 minutos y 15 preguntas que mide el grado de preparación de tu empresa para un proceso de venta."
    >
      {!result ? (
        <div className="mx-auto mt-14 max-w-2xl">
          {q ? (
            <>
              <div className="mb-8">
                <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gold" data-testid="test-progress-label">
                  Pregunta {idx + 1} de {total} · {bloques[q.bloque]}
                </p>
                <div className="mt-3 flex gap-1.5">
                  {questions.map((_, i) => (
                    <span key={i} className={`h-1 flex-1 transition-colors duration-300 ${i <= idx ? "bg-gold" : "bg-gold/20"}`} />
                  ))}
                </div>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.3 }}
                  data-testid={`test-question-${q.id}`}
                >
                  <p className="font-serif text-2xl leading-snug text-white sm:text-3xl">{q.texto}</p>
                  <div className="mt-8 space-y-3">
                    {q.opciones.map((op, i) => (
                      <button
                        key={i}
                        onClick={() => answer(q.id, i)}
                        data-testid={`test-opt-${q.id}-${i}`}
                        className={`w-full border px-6 py-4 text-left text-sm transition-all duration-300 ${
                          respuestas[q.id] === i
                            ? "border-gold bg-gold text-ink"
                            : "border-gold/20 bg-white/[0.03] text-slate-200 hover:border-gold/60 hover:text-gold"
                        }`}
                      >
                        {op}
                      </button>
                    ))}
                  </div>
                  {idx > 0 && (
                    <button
                      onClick={() => setIdx(idx - 1)}
                      data-testid="test-prev"
                      className="mt-6 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-400 transition-colors hover:text-gold"
                    >
                      <ArrowLeft className="h-4 w-4" /> Anterior
                    </button>
                  )}
                </motion.div>
              </AnimatePresence>
              {loading && <p className="mt-8 text-sm text-gold">Calculando tu diagnóstico...</p>}
            </>
          ) : (
            <p className="text-sm text-slate-400">Cargando el test...</p>
          )}
          <p className="mt-10 text-xs leading-relaxed text-slate-500">{LEGAL}</p>
        </div>
      ) : (
        <div className="mt-14" data-testid="test-result">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <div className="relative border border-gold/20 bg-ink-3/60 p-8 text-center md:p-10">
              <div className="pointer-events-none absolute -left-2 -top-2 h-5 w-5 border-l border-t border-gold" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Índice global de preparación</p>
              <div className="relative mx-auto mt-6 h-36 w-36">
                <div
                  className="h-36 w-36 rounded-full"
                  style={{ background: `conic-gradient(#C0A062 ${result.indice * 3.6}deg, rgba(192,160,98,0.15) 0deg)` }}
                />
                <div className="absolute inset-3 flex items-center justify-center rounded-full bg-ink-3">
                  <p className="font-serif text-4xl text-white" data-testid="test-indice">{result.indice}</p>
                </div>
              </div>
              <p className="mt-6 font-serif text-xl leading-snug text-white" data-testid="test-nivel">{result.nivel}</p>
              <div className="mt-8 space-y-3 text-left">
                {result.bloques.map((b) => (
                  <div key={b.nombre} className="flex items-center justify-between gap-3 border-b border-gold/10 pb-3" data-testid={`test-bloque-${b.nombre}`}>
                    <span className="flex items-center gap-2.5 text-sm text-slate-300">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: SEMAFORO[b.semaforo] }} />
                      {b.nombre}
                    </span>
                    <span className="font-serif text-lg text-gold-light">{b.pct} %</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-8">
              <Radar bloques={result.bloques} />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Tus 3 puntos débiles principales</p>
                <div className="mt-5 space-y-4">
                  {result.debiles.map((d) => (
                    <div key={d.texto} className="border border-gold/20 bg-ink-3/60 p-6" data-testid="test-weak">
                      <p className="text-sm font-semibold text-white">{d.texto}</p>
                      <p className="mt-2 text-sm leading-relaxed text-slate-400">{d.porque}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-20 border-t border-gold/10 pt-16">
            <div className="mx-auto max-w-2xl">
              {done ? (
                <DonePanel
                  testid="test-done"
                  title="Hemos enviado el plan a tu email."
                  subtitle="Un socio de AJG te contactará en 24-48 h para comentar el diagnóstico de forma confidencial."
                  extra={
                    <button
                      onClick={() => { navigate("/"); setTimeout(() => document.querySelector("#servicios")?.scrollIntoView({ behavior: "smooth" }), 450); }}
                      data-testid="test-servicios-link"
                      className="mt-8 border border-gold/60 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-all hover:bg-gold hover:text-ink"
                    >
                      Conoce cómo preparamos empresas para la venta
                    </button>
                  }
                />
              ) : (
                <form onSubmit={submitLead} className="space-y-5" data-testid="test-lead-form">
                  <h2 className="font-serif text-3xl text-white">Recibe tu plan de preparación para la venta</h2>
                  <p className="text-sm leading-relaxed text-slate-300">
                    Diagnóstico por áreas, acciones recomendadas para cada punto débil y calendario orientativo según tu horizonte, en PDF.
                  </p>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Nombre">
                      <input data-testid="test-nombre" value={lead.nombre} onChange={(e) => setLead({ ...lead, nombre: e.target.value })} required className={inputCls} />
                    </Field>
                    <Field label="Empresa">
                      <input data-testid="test-empresa" value={lead.empresa} onChange={(e) => setLead({ ...lead, empresa: e.target.value })} required className={inputCls} />
                    </Field>
                  </div>
                  <Field label="Sector">
                    <select data-testid="test-sector" value={lead.sector} onChange={(e) => setLead({ ...lead, sector: e.target.value })} required className={selectCls}>
                      <option value="" disabled className="bg-ink-2">Selecciona una opción</option>
                      {["Industrial y manufactura", "Distribución y comercio mayorista", "Construcción", "Ingeniería y servicios técnicos", "Servicios profesionales", "Tecnología y software", "Salud y servicios sanitarios", "Transporte y logística", "Alimentación y bebidas", "Hostelería y restauración", "Comercio minorista", "Medioambiente y gestión de residuos", "Alquiler de maquinaria y equipos", "Educación y formación", "Otros"].map((s) => (
                        <option key={s} value={s} className="bg-ink-2 text-white">{s}</option>
                      ))}
                    </select>
                  </Field>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Email">
                      <input data-testid="test-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required className={inputCls} />
                    </Field>
                    <Field label="Teléfono">
                      <input data-testid="test-telefono" type="tel" value={lead.telefono} onChange={(e) => setLead({ ...lead, telefono: e.target.value })} required className={inputCls} />
                    </Field>
                  </div>
                  <ConsentChecks lead={lead} setLead={setLead} prefix="test" />
                  <GoldButton loading={loading} testid="test-lead-submit">Recibir mi plan de preparación</GoldButton>
                  <p className="text-xs leading-relaxed text-slate-500">{LEGAL}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </ToolShell>
  );
}
